import {
  Badge,
  Box,
  Flex,
  IconButton,
  Input,
  Table,
  Text,
} from '@chakra-ui/react';
import { Button, SectionCard, ToggleButton } from '@themes/aegix';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { RenderPhoneInputArgs } from './ProfilePersonalSection';

/**
 * Self-service mass-notification destinations (ADR-141 §5).
 *
 * Presentational per the profile-ui contract: the adapter owns the
 * `/api/v2/me/contact*` mutations, toaster, and refetching. Self-added
 * destinations surface a "pending verification" state (§6): email offers a
 * resend affordance, and phone destinations render the host-supplied inline
 * OTP verify surface through the `renderDestinationVerification` slot
 * (wired by AIM's `ContactDestinationVerifyPanel`).
 *
 * Hosts without a self-serve phone path (SMS, ONE) omit the slot and phone
 * rows degrade to the legacy "ask an admin to verify" affordance — the lib
 * stays free of the app-scoped verify hook / react-query / services so all
 * three consumers keep sharing one section.
 */

export interface ProfileContactDestination {
  id: number;
  channel_code: string;
  address: string;
  label?: string;
  priority: number;
  enabled: boolean;
  verified_at: string | null;
  opt_out_at: string | null;
  is_sendable: boolean;
}

export interface ProfileContactChannelOption {
  code: string;
  label: string;
  addressPlaceholder: string;
  /** What the address field holds — lets the host swap in a richer editor
   * for phone-kind channels. Omitted = plain text input. */
  addressKind?: 'mail' | 'phone';
}

export const DEFAULT_CONTACT_CHANNEL_OPTIONS: ProfileContactChannelOption[] = [
  { code: 'mail', label: 'Email', addressPlaceholder: 'you@example.org', addressKind: 'mail' },
  { code: 'rcs', label: 'Text message', addressPlaceholder: '+1 801 555 0100', addressKind: 'phone' },
  { code: 'voice', label: 'Voice call', addressPlaceholder: '+1 801 555 0100', addressKind: 'phone' },
];

export interface ProfileContactSectionProps {
  destinations: ProfileContactDestination[];
  channelOptions?: ProfileContactChannelOption[];
  onAdd: (channelCode: string, address: string) => void;
  onToggle: (id: number, enabled: boolean) => void;
  onRemove: (id: number) => void;
  onResendVerification: (id: number) => void;
  /**
   * Optional host-supplied inline verify surface for a pending, non-mail
   * (phone) destination — AIM injects its `ContactDestinationVerifyPanel`
   * here so the OTP code-entry flow sits on the row itself instead of a
   * detached block below the card.
   *
   * Called once per pending, non-opted-out, non-mail destination. Return a
   * node to render it in place of the legacy "ask an admin to verify" button;
   * return `null`/`undefined` (or omit the prop entirely) to fall back to that
   * button. Hosts that only self-verify some channel kinds (e.g. SMS/voice but
   * not webhooks) return `null` for the rest and the row degrades gracefully.
   */
  renderDestinationVerification?: (
    dest: ProfileContactDestination,
  ) => ReactNode;
  /**
   * Optional host-supplied address editor for phone-kind channels —
   * AIM injects its react-phone-number-input control (country selector, US
   * default) so the add form matches personal details. Same contract as the
   * personal section's `renderPhoneInput`; the lib stays free of
   * react-phone-number-input. Omit and phone channels keep the
   * plain input (SMS/ONE path).
   */
  renderPhoneAddressInput?: (args: RenderPhoneInputArgs) => ReactNode;
  /**
   * The account's personal-details phone (E.164), if any. When set,
   * the section always surfaces it: as a synthesized non-removable row with
   * the toggle OFF while no matching destination exists (toggling ON creates
   * a real destination via `onAdd`), or by marking the matching real row
   * non-removable (the number is managed in personal details, not here).
   * Matching is exact E.164 — both sides store canonical E.164 server-side.
   */
  personalPhone?: string | null;
  isSaving?: boolean;
  title?: string;
  e2eTitle?: string;
}

export function ProfileContactSection({
  destinations,
  channelOptions = DEFAULT_CONTACT_CHANNEL_OPTIONS,
  onAdd,
  onToggle,
  onRemove,
  onResendVerification,
  renderDestinationVerification,
  renderPhoneAddressInput,
  personalPhone = null,
  isSaving = false,
  title,
  e2eTitle = 'profileContactSection',
}: ProfileContactSectionProps) {
  const { t } = useTranslation();
  const [channelCode, setChannelCode] = useState(
    channelOptions[0]?.code ?? 'mail',
  );
  const [address, setAddress] = useState('');
  const selected = channelOptions.find((o) => o.code === channelCode);

  // The personal-details phone always has a presence here.
  const accountPhone = (personalPhone || '').trim() || null;
  const accountDest = accountPhone
    ? destinations.find((d) => d.address === accountPhone)
    : undefined;
  const showAccountRow = !!accountPhone && !accountDest;
  const accountPhoneChannel =
    channelOptions.find((o) => o.addressKind === 'phone')?.code ?? 'rcs';

  const handleAdd = () => {
    if (!address.trim()) return;
    onAdd(channelCode, address.trim());
    setAddress('');
  };

  /**
   * Pending (unverified, not opted-out) affordance for a destination.
   * Mail keeps its "send verification email" resend button. Phone/other
   * kinds prefer the host's inline verify panel (self-serve OTP)
   * and fall back to the legacy admin-ask button when no panel is supplied.
   */
  const renderPendingAffordance = (dest: ProfileContactDestination) => {
    if (dest.channel_code === 'mail') {
      return (
        <Button
          data-e2e={`resendVerification-${dest.id}`}
          size="sm"
          visual="secondary"
          mt="1"
          onClick={() => onResendVerification(dest.id)}
        >
          {t('profile.sendVerificationEmail', 'Send verification email')}
        </Button>
      );
    }

    const inlineVerify = renderDestinationVerification?.(dest);
    if (inlineVerify != null) {
      return (
        <Box mt="2" data-e2e={`destinationVerifyInline-${dest.id}`}>
          {inlineVerify}
        </Box>
      );
    }

    return (
      <Button
        data-e2e={`resendVerification-${dest.id}`}
        size="sm"
        visual="secondary"
        mt="1"
        onClick={() => onResendVerification(dest.id)}
      >
        {t('profile.askAdminToVerify', 'Ask an admin to verify')}
      </Button>
    );
  };

  return (
    <SectionCard
      title={title ?? t('profile.notificationDestinations', 'Notification destinations')}
      e2eTitle={e2eTitle}
    >
      <Table.Root>
        <Table.Body>
          {showAccountRow && (
            <Table.Row bg="surface.primary" data-e2e="accountPhoneRow">
              <Table.Cell>
                <Text fontWeight="medium">{accountPhone}</Text>
                <Flex gap="1" mt="1" align="center">
                  <Badge size="sm" data-e2e="accountPhoneBadge">
                    {t('profile.accountPhone', 'Account phone')}
                  </Badge>
                </Flex>
                <Text color="fg.muted" fontSize="xs" mt="1">
                  {t(
                    'profile.accountPhoneHint',
                    'From your personal details — turn on to receive mass notifications at this number.',
                  )}
                </Text>
              </Table.Cell>
              <Table.Cell textAlign="right">
                <Flex gap="2" justify="flex-end" align="center">
                  <ToggleButton
                    e2eTitle="accountPhoneEnabled"
                    checked={false}
                    disabled={isSaving}
                    onCheckedChange={(details: { checked: boolean }) => {
                      if (details.checked && accountPhone) {
                        onAdd(accountPhoneChannel, accountPhone);
                      }
                    }}
                  />
                  {/* No remove affordance — the number is managed in
                      personal details. */}
                </Flex>
              </Table.Cell>
            </Table.Row>
          )}
          {destinations.map((dest) => (
            <Table.Row
              key={dest.id}
              bg="surface.primary"
              data-e2e="profileContactDestinationRow"
            >
              <Table.Cell>
                <Text fontWeight="medium">{dest.address}</Text>
                <Flex gap="1" mt="1" align="center">
                  <Badge size="sm">
                    {t(
                      `profile.channel.${dest.channel_code}`,
                      channelOptions.find((o) => o.code === dest.channel_code)?.label ?? dest.channel_code,
                    )}
                  </Badge>
                  {dest.opt_out_at ? (
                    <Badge
                      size="sm"
                      colorPalette="red"
                      data-e2e="destinationOptedOut"
                    >
                      {t('profile.optedOut', 'Opted out')}
                    </Badge>
                  ) : dest.verified_at ? (
                    <Badge
                      size="sm"
                      colorPalette="green"
                      data-e2e="destinationVerified"
                    >
                      {t('profile.verified', 'Verified')}
                    </Badge>
                  ) : (
                    <Badge
                      size="sm"
                      colorPalette="orange"
                      data-e2e="destinationPending"
                    >
                      {t('profile.pendingVerification', 'Pending verification')}
                    </Badge>
                  )}
                </Flex>
                {!dest.verified_at &&
                  !dest.opt_out_at &&
                  renderPendingAffordance(dest)}
              </Table.Cell>
              <Table.Cell textAlign="right">
                <Flex gap="2" justify="flex-end" align="center">
                  <ToggleButton
                    e2eTitle={`destinationEnabled-${dest.id}`}
                    checked={dest.enabled}
                    disabled={isSaving || !!dest.opt_out_at}
                    onCheckedChange={(details: { checked: boolean }) =>
                      onToggle(dest.id, details.checked)
                    }
                  />
                  {dest.id === accountDest?.id ? (
                    /* The account-phone destination is managed via
                       personal details — removable only there. */
                    <Badge size="sm" data-e2e="accountPhoneBadge">
                      {t('profile.accountPhone', 'Account phone')}
                    </Badge>
                  ) : (
                    <IconButton
                      aria-label={t('profile.removeDestination', 'Remove destination')}
                      data-e2e={`removeDestination-${dest.id}`}
                      size="xs"
                      variant="ghost"
                      disabled={isSaving}
                      onClick={() => onRemove(dest.id)}
                    >
                      ✕
                    </IconButton>
                  )}
                </Flex>
              </Table.Cell>
            </Table.Row>
          ))}
          {destinations.length === 0 && !showAccountRow && (
            <Table.Row bg="surface.primary">
              <Table.Cell colSpan={2}>
                <Text color="fg.muted">
                  {t(
                    'profile.noDestinations',
                    'No destinations yet — add one below to receive mass notifications.',
                  )}
                </Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
      <Flex gap="2" mt="3" align="center" wrap="wrap">
        <select
          data-e2e="addDestinationChannel"
          value={channelCode}
          onChange={(e) => setChannelCode(e.target.value)}
          disabled={isSaving}
        >
          {channelOptions.map((o) => (
            <option key={o.code} value={o.code}>
              {t(`profile.channel.${o.code}`, o.label)}
            </option>
          ))}
        </select>
        {selected?.addressKind === 'phone' && renderPhoneAddressInput ? (
          <Box flex="1" minW="12rem">
            {renderPhoneAddressInput({
              value: address,
              onChange: setAddress,
              disabled: isSaving,
              'data-e2e': 'addDestinationAddress',
            })}
          </Box>
        ) : (
          <Input
            data-e2e="addDestinationAddress"
            flex="1"
            minW="12rem"
            size="sm"
            placeholder={selected?.addressPlaceholder}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={isSaving}
          />
        )}
        <Button
          data-e2e="addDestination"
          size="sm"
          disabled={isSaving || !address.trim()}
          onClick={handleAdd}
        >
          {t('common.add', t('add', 'Add'))}
        </Button>
      </Flex>
      {/* TCPA consent disclosure — also the opt-in evidence for the toll-free
          carrier registration. STOP/HELP are honored upstream by the
          AWS-managed opt-out list, so this copy must not drift from that. */}
      <Text data-e2e="contactConsentCaption" color="fg.muted" fontSize="xs" mt="2">
        {t(
          'profile.contactConsentCaption',
          'By adding a phone number you agree to receive safety notification texts from your organization. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
        )}
      </Text>
    </SectionCard>
  );
}
